import { Caption } from '@vizit/atoms';
import { getFilledZapCount } from '@vizit/constants';
import { ImageSparkIcon } from '@vizit/icons';
import {
  AreaMetricCard,
  AssetCountMetricCard,
  BaseMetricCard,
  BooleanMetricCard,
  LibraryEmptyState,
  VisualDiversityMetricCard,
} from '@vizit/molecules';
import type { AreaMetric, BarMetric, BooleanMetric, Metric } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { RecommendationsExplain } from '../recommendations-explain';
import { getMetricDescription } from './metric-descriptions';

interface MetricsRecommendationPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Array of metrics to display
   */
  metrics: Metric[];
  /**
   * Title for the panel
   * @default 'Recommendations for your category'
   */
  title?: string;
  /**
   * The category name to display in the recommendations explanation tooltip
   */
  categoryName?: string;
  /**
   * Callback when the reorder button is clicked on boolean metrics
   */
  onReorder?: (metricKey: string) => void;
  /**
   * Width of each chart
   * @default 300
   */
  chartWidth?: number;
  /**
   * Height of each chart
   * @default 60
   */
  chartHeight?: number;
  /**
   * Whether the metrics are loading
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether the current view is showing the original PDP snapshot
   * True when viewMode === ORIGINAL and originalSnapshot data is available
   */
  isOriginalView?: boolean;
  /**
   * The number of carousel images
   * Used to determine if empty state should be shown
   */
  carouselImageCount?: number;
  /**
   * The source type of the product (manual or scraped)
   * Used to determine if empty state should be shown
   */
  sourceType?: 'manual' | 'scraped' | null;
  /**
   * Whether the PDP is currently polling for updates
   * Used to determine if empty state should be shown for scraped products
   * @default false
   */
  isPolling?: boolean;
  /**
   * Whether the reorder mutation is in progress
   * @default false
   */
  isReordering?: boolean;
}

const PLACEHOLDER_COUNT = 4;

/**
 * Defines the static order in which metrics should be displayed
 */
const METRIC_DISPLAY_ORDER = ['avg_carousel_score', 'visual_diversity', 'n_carousel_images', 'order_score'] as const;

/**
 * Sorts metrics according to the predefined display order
 */
function sortMetricsByDisplayOrder(metrics: Metric[]): Metric[] {
  return [...metrics].sort((a, b) => {
    const indexA = METRIC_DISPLAY_ORDER.indexOf(a.key as (typeof METRIC_DISPLAY_ORDER)[number]);
    const indexB = METRIC_DISPLAY_ORDER.indexOf(b.key as (typeof METRIC_DISPLAY_ORDER)[number]);

    // If metric not found in order array, push to end
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
}

/**
 * MetricsRecommendationPanel displays a collection of specialized metric cards
 * with an optional caption and tooltip
 */
const MetricsRecommendationPanel = React.forwardRef<HTMLDivElement, MetricsRecommendationPanelProps>(
  (props, forwardedRef) => {
    const {
      metrics,
      title = 'Recommendations for your category',
      categoryName,
      onReorder,
      chartWidth,
      chartHeight,
      isLoading = false,
      className,
      isOriginalView = false,
      carouselImageCount = 0,
      sourceType = 'manual',
      isPolling = false,
      isReordering = false,
      ...other
    } = props;

    const renderMetricCard = (metric: Metric, cardIsLoading = false) => {
      const filledZapCount = getFilledZapCount(metric.impactDelta);
      const description = getMetricDescription(metric);

      switch (metric.key) {
        case 'order_score':
          return (
            <BooleanMetricCard
              key={metric.key}
              metric={metric as BooleanMetric}
              description={description}
              filledZapCount={filledZapCount}
              chartWidth={chartWidth}
              chartHeight={chartHeight}
              onReorder={() => onReorder?.(metric.key)}
              isLoading={cardIsLoading}
              isReordering={isReordering}
            />
          );

        case 'n_carousel_images':
          return (
            <AssetCountMetricCard
              key={metric.key}
              metric={metric as BarMetric}
              description={description}
              filledZapCount={filledZapCount}
              chartWidth={chartWidth}
              chartHeight={chartHeight}
              isLoading={cardIsLoading}
            />
          );

        case 'visual_diversity':
          return (
            <VisualDiversityMetricCard
              key={metric.key}
              metric={metric as BarMetric}
              description={description}
              filledZapCount={filledZapCount}
              chartWidth={chartWidth}
              chartHeight={chartHeight}
              isLoading={cardIsLoading}
            />
          );

        case 'avg_carousel_score':
          return (
            <AreaMetricCard
              key={metric.key}
              metric={metric as AreaMetric}
              description={description}
              filledZapCount={filledZapCount}
              chartWidth={chartWidth}
              chartHeight={chartHeight}
              isLoading={cardIsLoading}
            />
          );

        default:
          return null;
      }
    };

    // Show empty state only for manual products with no carousel images
    // Scraped products and polling states show loading skeletons instead
    if (!isLoading && !isPolling && carouselImageCount === 0 && sourceType === 'manual') {
      return (
        <LibraryEmptyState
          heading="No carousel images yet"
          description="Upload assets to get started"
          icon={ImageSparkIcon}
        />
      );
    }

    const renderGridContent = () => {
      if (isOriginalView) {
        return Array.from({ length: PLACEHOLDER_COUNT }, (_, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list, no reordering
            key={`placeholder-${i}`}
            data-testid="metric-placeholder"
            className="min-h-[121.4px] rounded-sm bg-gray-light-25"
          />
        ));
      }

      // Show loading skeletons when polling, even if we have metrics
      if (metrics && !isLoading && !isPolling && metrics.length > 0) {
        const sortedMetrics = sortMetricsByDisplayOrder(metrics);
        return sortedMetrics.map((metric) => renderMetricCard(metric, isLoading));
      }

      return Array.from({ length: PLACEHOLDER_COUNT }).map((_, index) => (
        <BaseMetricCard
          // biome-ignore lint/suspicious/noArrayIndexKey: it's ok here
          key={`loading-${index}`}
          label=""
          filledZapCount={0}
          isLoading={true}
        >
          {null}
        </BaseMetricCard>
      ));
    };

    return (
      <div ref={forwardedRef} className={cx('w-full', className)} {...other}>
        <Caption label={title} infoCustomComponent={<RecommendationsExplain categoryName={categoryName} />} gap="gap-8">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">{renderGridContent()}</div>
        </Caption>
      </div>
    );
  },
);

export type { MetricsRecommendationPanelProps };
export { MetricsRecommendationPanel };
